// World doors — the local world graph (DOR-1..5, M41). A save holds many worlds
// (IndexedDB `worlds` rows); a `world_door` block entity is a directed edge between
// two of them. This module is the client-side graph: enumerate edges, create + bind
// child worlds, resize under refuse-shrink-past-builds, and walk the home chain for
// the escape hatch. It never touches the sim (ENG-1) — all of it is IDB orchestration
// over the pure `WorldDoorData` strings the block entity stores.
//
// Traversal itself is a world-session swap (DOR-2): the game flushes, tears the whole
// session down via a page navigation to `?world=<targetId>`, and re-enters `startGame`
// carrying the player's inventory in a session-storage handoff (see world/handoff.ts +
// boot's arrival logic). The parent never loads the child, so the TARDIS rule and legal
// cycles (E-36) hold by construction — a full teardown cannot recurse the loader.
import type { SaveManager, WorldMeta, WorldType } from '../storage/saves';
import type { SavedBlockEntity, WorldDoorData } from '../../sim/game/blockentity';
import { DOOR, EARTH } from '../../sim/config';
import { earthRangeSpec } from '../earth';

/** A directed edge in the world graph: a bound `world_door` at (x,y,z) in `worldId`
 *  pointing at `targetDoorId` in `targetWorldId` (DOR-1). */
export interface DoorEdge {
  worldId: string;
  dim: string;
  x: number;
  y: number;
  z: number;
  doorId: string;
  targetWorldId: string;
  targetDoorId: string;
}

export interface WorldGraph {
  worlds: WorldMeta[];
  edges: DoorEdge[];
}

/** A fresh unique id. `crypto.randomUUID` in every supported runtime; a deterministic-
 *  enough fallback keeps tests/jsdom without it working. */
export function newId(prefix: string): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}:${uuid}`;
}

/** Clamp a requested child footprint (blocks per side) to the sane range. */
export function clampSize(size: number): number {
  if (!Number.isFinite(size)) return DOOR.CHILD_DEFAULT_SIZE;
  return Math.max(DOOR.MIN_SIZE, Math.min(DOOR.MAX_SIZE, Math.floor(size)));
}

/** Enumerate the world graph (DOR-1): every world is a node; every *bound* `world_door`
 *  block entity is a directed edge. Unbound doors (empty `targetWorldId`) are omitted. */
export async function worldGraph(save: SaveManager): Promise<WorldGraph> {
  const worlds = await save.listWorlds();
  const edges: DoorEdge[] = [];
  for (const w of worlds) {
    for (const { dim, list } of await save.allBlockEntities(w.id)) {
      for (const be of list) {
        if (be.type !== 'world_door') continue;
        const d = be.data as WorldDoorData;
        if (!d.targetWorldId) continue;
        edges.push({
          worldId: w.id, dim, x: be.x, y: be.y, z: be.z,
          doorId: d.doorId, targetWorldId: d.targetWorldId, targetDoorId: d.targetDoorId,
        });
      }
    }
  }
  return { worlds, edges };
}

export interface ChildOpts {
  worldType: WorldType;
  /** Square footprint (blocks per side); clamped, default `DOOR.CHILD_DEFAULT_SIZE`. */
  size: number;
  name?: string;
  seed?: string;
  gameMode?: 'survival' | 'creative';
  /** Server worlds only (M42, DOR-6): private worlds admit only the owner + ACL grants;
   *  ignored by local (IDB) worlds, which have no access control. */
  public?: boolean;
}

/** Build a child world's meta (DOR-3): bounded, homed to its parent, and carrying the
 *  paired return door as a pending edge (materialised at the child's spawn on first
 *  entry). Pure — the caller persists it. Earth children default to the offline KHFD. */
export function buildChildMeta(
  parent: WorldMeta,
  opts: ChildOpts,
  ids: { childId: string; returnDoorId: string; parentDoorId: string },
): WorldMeta {
  const size = clampSize(opts.size);
  const wt = opts.worldType;
  const now = Date.now();
  return {
    id: ids.childId,
    name: opts.name?.trim() || 'World',
    seed: opts.seed?.trim() || String(now),
    worldType: wt,
    gameMode: opts.gameMode ?? 'creative',
    difficulty: parent.difficulty,
    allowCommands: parent.allowCommands ?? true,
    bounds: { size },
    homeWorldId: parent.id,
    pendingDoors: [{ doorId: ids.returnDoorId, targetWorldId: parent.id, targetDoorId: ids.parentDoorId }],
    aviation: wt === 'atlas' || wt === 'earth' ? true : undefined,
    earth: wt === 'earth'
      ? { lat: EARTH.DEFAULT.lat, lon: EARTH.DEFAULT.lon, label: EARTH.DEFAULT.label, scaleM: EARTH.DEFAULT.scaleM, extentM: EARTH.EXTENT_M_DEFAULT, contract: { major: 1 } }
      : undefined,
    ranges: wt === 'earth' ? { overworld: earthRangeSpec(EARTH.DEFAULT.scaleM) } : undefined,
    createdAt: now,
    playedAt: now,
    version: 1,
  };
}

/** The new door state to write back after a bind + the id of the world it now links. */
export interface BindResult {
  childId: string;
  door: WorldDoorData;
}

/**
 * Create a child world for an unbound door and bind them atomically (DOR-1/3). The
 * child meta is written **first**; the returned door data is only applied by the caller
 * (to the live block entity, then `saveNow`) once the write resolved — so a failure
 * leaves neither edge (both-or-neither, over the E-21 local-IDB semantics).
 */
export async function createChildWorld(
  save: SaveManager,
  parent: WorldMeta,
  door: WorldDoorData,
  opts: ChildOpts,
): Promise<BindResult> {
  const childId = newId('w');
  const returnDoorId = newId('d');
  const parentDoorId = door.doorId || newId('d');
  const child = buildChildMeta(parent, opts, { childId, returnDoorId, parentDoorId });
  await save.putWorld(child); // write the child first
  return { childId, door: { doorId: parentDoorId, targetWorldId: childId, targetDoorId: returnDoorId } };
}

/** Bind an unbound door to an already-existing world (DOR-1). Registers the paired
 *  return door as pending on the target so it materialises there on next entry. */
export async function bindExistingWorld(
  save: SaveManager,
  parent: WorldMeta,
  door: WorldDoorData,
  targetWorldId: string,
): Promise<BindResult | { error: string }> {
  const target = await save.getWorld(targetWorldId);
  if (!target) return { error: 'world not found' };
  const parentDoorId = door.doorId || newId('d');
  const returnDoorId = newId('d');
  const pendingDoors = [...(target.pendingDoors ?? []), { doorId: returnDoorId, targetWorldId: parent.id, targetDoorId: parentDoorId }];
  await save.putWorld({ ...target, pendingDoors });
  return { childId: targetWorldId, door: { doorId: parentDoorId, targetWorldId, targetDoorId: returnDoorId } };
}

export interface ResizeBlocker {
  kind: 'section' | 'block-entity' | 'entity';
  x: number;
  z: number;
}
export interface ResizeResult {
  ok: boolean;
  size: number;
  blockers?: ResizeBlocker[];
}

/** A point is outside a `size`-side square centred on the origin. */
function pointOutside(x: number, z: number, half: number): boolean {
  return x < -half || x >= half || z < -half || z >= half;
}

/** A 16×16 chunk is *entirely* outside the bounds (its nearest cell is beyond an edge). */
function chunkFullyOutside(cx: number, cz: number, half: number): boolean {
  const xMin = cx * 16, xMax = cx * 16 + 15, zMin = cz * 16, zMax = cz * 16 + 15;
  return xMin >= half || xMax < -half || zMin >= half || zMax < -half;
}

/** Every edited section / block entity / persisted entity that lies outside a proposed
 *  `size` footprint (DOR-4 shrink blockers). Sections block only when the whole chunk is
 *  beyond an edge; block entities + entities block at their exact position. */
export async function outsideBounds(save: SaveManager, worldId: string, size: number): Promise<ResizeBlocker[]> {
  const half = size / 2;
  const blockers: ResizeBlocker[] = [];
  for (const s of await save.sectionRows(worldId)) {
    if (chunkFullyOutside(s.cx, s.cz, half)) blockers.push({ kind: 'section', x: s.cx * 16, z: s.cz * 16 });
  }
  for (const { list } of await save.allBlockEntities(worldId)) {
    for (const be of list) if (pointOutside(be.x, be.z, half)) blockers.push({ kind: 'block-entity', x: be.x, z: be.z });
  }
  for (const { list } of await save.allEntities(worldId)) {
    for (const e of list as SavedBlockEntity[]) if (pointOutside(e.x, e.z, half)) blockers.push({ kind: 'entity', x: Math.floor(e.x), z: Math.floor(e.z) });
  }
  return blockers;
}

/** Resize a world's bounded footprint (DOR-4). Growth (or no change) always succeeds; a
 *  shrink is REJECTED while any edited section / block entity / entity lies outside the
 *  new bounds — the refusal lists the blockers so the owner can clear them (lossless). */
export async function resizeWorld(save: SaveManager, worldId: string, newSize: number): Promise<ResizeResult> {
  const meta = await save.getWorld(worldId);
  if (!meta) return { ok: false, size: 0 };
  const size = clampSize(newSize);
  const oldSize = meta.bounds?.size ?? size;
  if (size >= oldSize) {
    await save.putWorld({ ...meta, bounds: { size } });
    return { ok: true, size };
  }
  const blockers = await outsideBounds(save, worldId, size);
  if (blockers.length) return { ok: false, size: oldSize, blockers };
  await save.putWorld({ ...meta, bounds: { size } });
  return { ok: true, size };
}

/** Walk the `homeWorldId` chain to the first reachable root (DOR-5 escape hatch): a
 *  world with no home, or the highest ancestor still present. Returns the current id
 *  when there's nowhere to go (already root, or the whole chain was deleted). */
export async function homeWorldId(save: SaveManager, current: WorldMeta): Promise<string> {
  const seen = new Set<string>([current.id]);
  let meta: WorldMeta | undefined = current;
  let best = current.id;
  while (meta?.homeWorldId && !seen.has(meta.homeWorldId)) {
    const parentId = meta.homeWorldId;
    seen.add(parentId);
    const parent = await save.getWorld(parentId);
    if (!parent) break; // parent deleted → the highest reachable ancestor is `best`
    best = parent.id;
    meta = parent;
  }
  return best;
}

/** Find a bound door's target world + return-anchor door across the graph (helper for a
 *  UI that shows where a door leads). Returns undefined for unbound / missing targets. */
export function isBound(door: WorldDoorData | undefined): boolean {
  return !!door && door.targetWorldId !== '';
}
